import { CopyButton, PageSection, PageWrapper, Share } from '@src/components';
import { ProgressWithMilestone } from './ProgressWithMilestone';
import { InfoCircle, LinkCircle } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { ReferralRules } from './ReferralRules';
import { useState } from 'react';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import { Button, Tooltip } from 'antd';
import { BugIcon } from '@src/components/svg';
import { ReferralsList } from './ReferralsList/ReferralsList';
import { useGetAffiliateDashboardQuery } from '@src/services/affiliate/affiliateApiSlice';
import { cleanNumberDisplay, getCurrencySymbol } from '@src/utilities/currency';
import {
  BASE_TIER_THRESHOLD,
  MAX_THRESHOLD_MULTIPLIER,
} from '@src/constants/referral';

export function DashboardView() {
  const { t } = useTranslation();
  const [openReferralRule, setOpenReferralRule] = useState(false);

  const { data, isLoading, isFetching } = useGetAffiliateDashboardQuery({});

  return (
    <PageWrapper loading={isLoading || isFetching} rootClassName='space-y-6'>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <PageSection className='bg-wl-background p-4 space-y-4'>
          <div className='text-M-16 text-wl-on-surface'>
            {t('pages.affiliate.dashboard.totalEarned')}
          </div>

          <PageSection className='bg-wl-surface text-wl-on-surface text-B-20'>
            {data?.totalEarned
              ? `${getCurrencySymbol('EUR')} ${cleanNumberDisplay(data.totalEarned)}`
              : '0'}
          </PageSection>
        </PageSection>

        <PageSection className='bg-wl-background p-4'>
          <div className='text-M-16 text-wl-on-surface'>
            {t('pages.affiliate.dashboard.commissionLevel')}
          </div>
          <ProgressWithMilestone
            current={data?.totalSalesGenerated ?? 0}
            max={
              (data?.tierStructure[2]?.salesThreshold ?? BASE_TIER_THRESHOLD) *
              MAX_THRESHOLD_MULTIPLIER
            }
            milestones={[
              {
                value: data?.tierStructure[0]?.salesThreshold ?? 0,
                percent: data?.tierStructure[0]?.commissionRate ?? 0,
              },
              {
                value: data?.tierStructure[1]?.salesThreshold ?? 0,
                percent: data?.tierStructure[1]?.commissionRate ?? 0,
              },
              {
                value: data?.tierStructure[2]?.salesThreshold ?? 0,
                percent: data?.tierStructure[2]?.commissionRate ?? 0,
              },
            ]}
          />
        </PageSection>

        <PageSection className='bg-wl-background p-4 space-y-4'>
          <div className='text-M-16 text-wl-on-surface'>
            {t('pages.affiliate.dashboard.availableBalance')}
          </div>
          <PageSection className='bg-wl-surface text-wl-on-surface text-B-20'>
            {data?.availableBalance
              ? `${getCurrencySymbol('EUR')} ${cleanNumberDisplay(data.availableBalance)}`
              : '0'}
          </PageSection>
        </PageSection>

        <PageSection className='bg-wl-background p-4 space-y-4'>
          <div className='text-M-16 text-wl-on-surface'>
            {t('pages.affiliate.dashboard.paidOut')}
          </div>
          <PageSection className='bg-wl-surface text-wl-on-surface text-B-20'>
            {data?.paidOut
              ? `${getCurrencySymbol('EUR')} ${cleanNumberDisplay(data.paidOut)}`
              : '0'}
          </PageSection>
        </PageSection>
      </div>

      <PageSection className='flex flex-col col-span-2  sm:flex-row sm:justify-between sm:items-center bg-wl-background p-4 gap-4'>
        <div className='flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-auto'>
          <div className='flex items-center w-full sm:min-w-48'>
            <div className='mr-2'>
              <LinkCircle
                variant='Bold'
                height={26}
                className='text-wl-on-surface'
              />
            </div>
            <div className='text-M-16'>
              <div className='text-M-16 text-wl-on-surface'>
                {t('pages.affiliate.dashboard.referralCode')}
              </div>
              <div className='flex items-center gap-2'>
                <span className='text-wl-primary'> {data?.referralCode}</span>
                <CopyButton
                  className='text-wl-primary'
                  text={data?.referralCode}
                  successMessage={t('notification.success.copied', {
                    name: 'Referral code',
                  })}
                />
              </div>
            </div>
          </div>

          <div className='flex items-center w-full sm:min-w-48'>
            <div className='text-M-16'>
              <div className='text-M-16 text-wl-on-surface'>
                {t('pages.affiliate.dashboard.referralLink')}
              </div>
              <Tooltip title={data?.referralLink}>
                <div className='flex items-center gap-2'>
                  <span className='text-wl-primary whitespace-nowrap'>
                    {data?.referralLink
                      ? data.referralLink.length > 20
                        ? `${data.referralLink.slice(0, 20)}...`
                        : data.referralLink
                      : ''}
                  </span>
                  <CopyButton
                    className='text-wl-primary'
                    text={data?.referralLink}
                    successMessage={t('notification.success.copied', {
                      name: 'Referral link',
                    })}
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>

        {data?.referralLink && (
          <Share
            title={t('pages.affiliate.referrals.singularTitle')}
            url={`https://${data?.referralLink}`}
          />
        )}
      </PageSection>
      <PageSection className='bg-blue-50 dark:bg-wl-background text-wl-on-background p-6'>
        <span className='flex gap-2 text-B-16'>
          {t('pages.affiliate.dashboard.howItWorks')}
          <InfoCircle
            variant='Bulk'
            className='text-wl-on-background cursor-pointer'
            onClick={() => setOpenReferralRule(true)}
          />
        </span>
        <p className='text-M-20'>{t('pages.affiliate.dashboard.shareInfo')}</p>
      </PageSection>
      <PageSection>
        <ReferralsList />
      </PageSection>
      <ResponsiveModal
        isOpen={openReferralRule}
        title={t('pages.affiliate.referralRules.title')}
        headerIcon={<BugIcon className='text-wl-on-background' />}
        content={<ReferralRules />}
        onClose={() => setOpenReferralRule(false)}
        className='!w-[680px]'
        footer={
          <Button
            onClick={() => setOpenReferralRule(false)}
            size='large'
            className='btn-secondary w-full text-wl-on-background'
          >
            {t('buttons.close')}
          </Button>
        }
      />
    </PageWrapper>
  );
}
